import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  searchUsers,
  createUser,
  updateUser,
  toggleUserStatus,
  deleteUsers,
  listRoles,
  toUser,
  type User,
  type UserSearchParams,
  type UserCreateRequest,
  type UserEditRequest,
  type UserRoleOption,
} from "@/services/users";
import UsersTable from "./components/UsersTable";
import EditUserModal from "./components/EditUserModal";
import AddUserModal from "./components/AddUserModal";
import AdminPagination from "@/components/admin/AdminPagination";
import AdminLayout from "@/components/layout/AdminLayout";
import AdminPageTitle from "@/components/admin/AdminPageTitle";
import { useAuth } from "@/context/auth";
import { docCategoryApi } from "@/services/docTaxonomy";
import { usePlatformDgSyncStatus } from "./usePlatformDgSyncStatus";
import {
  buildUserSearchParams,
  type RegionFilter,
  type RoleFilter,
  type StatusFilter,
  type SourceFilter,
} from "./userFilters";

type ToastKind = "success" | "error";
type UsersPageMode = "add" | "edit";

interface UsersRouteState {
  user?: User;
  searchUser?: string;
  toast?: { msg: string; kind: ToastKind };
}

const PAGE_SIZE = 20;
const EDIT_LOOKUP_PAGE_SIZE = 200;

async function findUserById(id: string): Promise<User | null> {
  let lookupPage = 1;

  while (true) {
    const response = await searchUsers({ Page: lookupPage, Limit: EDIT_LOOKUP_PAGE_SIZE });
    const rows = response.data.Data ?? response.data.data ?? [];
    const match = rows.map(toUser).find((user) => user.id === id);
    if (match) return match;

    const count = response.data.Count ?? response.data.count ?? rows.length;
    const limit = response.data.Limit ?? response.data.limit ?? EDIT_LOOKUP_PAGE_SIZE;
    if (lookupPage * limit >= count || rows.length === 0) return null;
    lookupPage += 1;
  }
}

export default function UsersPage({ pageMode }: { pageMode?: UsersPageMode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams<{ userId: string }>();
  const { user: currentUser, refreshUser } = useAuth();
  const routeState = (location.state ?? {}) as UsersRouteState;
  const routeEditingUser =
    pageMode === "edit" && routeState.user?.id === userId ? routeState.user : null;
  const initialSearch = pageMode ? "" : (routeState.searchUser?.trim() ?? "");
  const [users, setUsers] = useState<User[]>([]);

  // 用户权限只存分类 ID，列表展示时现查名称，分类改名后自动跟随
  const [categoryNameById, setCategoryNameById] = useState<Record<string, string>>({});
  const [visibleToAllCategoryIds, setVisibleToAllCategoryIds] = useState<string[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [roleOptions, setRoleOptions] = useState<UserRoleOption[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [searchInput, setSearchInput] = useState(initialSearch);
  const [search, setSearch] = useState(initialSearch);

  const [roleFilter, setRoleFilter] = useState<RoleFilter>("All");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("All");
  const [regionFilter, setRegionFilter] = useState<RegionFilter>("All");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("All");
  const [generalSourceTypeFilter, setGeneralSourceTypeFilter] = useState("All");

  const [toast, setToast] = useState<{ msg: string; kind: ToastKind } | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(routeEditingUser);
  const [editLoading, setEditLoading] = useState(pageMode === "edit" && !routeEditingUser);
  const [editLoadUserId, setEditLoadUserId] = useState<string | null>(
    pageMode === "edit" ? (userId ?? null) : null,
  );
  const [editLoadError, setEditLoadError] = useState<string | null>(null);
  const [addModalError, setAddModalError] = useState<string | null>(null);
  const [editModalError, setEditModalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [statusActionUserId, setStatusActionUserId] = useState<string | null>(null);
  const [deleteActionUserId, setDeleteActionUserId] = useState<string | null>(null);

  const showToast = (msg: string, kind: ToastKind = "success") => {
    setToast({ msg, kind });
  };

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const buildParams = useCallback(
    (p: number, keyword = search): UserSearchParams => {
      return buildUserSearchParams({
        page: p,
        pageSize: PAGE_SIZE,
        keyword,
        role: roleFilter,
        status: statusFilter,
        region: regionFilter,
        generalSourceTypeId: generalSourceTypeFilter,
        source: sourceFilter,
      });
    },
    [generalSourceTypeFilter, regionFilter, roleFilter, search, statusFilter, sourceFilter],
  );

  const fetchList = useCallback(
    async (p: number, keyword = search) => {
      setLoading(true);
      try {
        const res = await searchUsers(buildParams(p, keyword));
        const rows = res.data.Data ?? res.data.data ?? [];
        setUsers(rows.map(toUser));
        setTotal(res.data.Count ?? res.data.count ?? 0);
        setPage(res.data.Page ?? res.data.page ?? p);
      } catch (err: any) {
        showToast(err?.response?.data?.message || "加载失败，请稍后重试", "error");
      } finally {
        setLoading(false);
      }
    },
    [buildParams, search],
  );

  const refreshAfterSync = useCallback(() => {
    void fetchList(page);
  }, [fetchList, page]);
  const { isSyncing, lastSyncTime } = usePlatformDgSyncStatus(refreshAfterSync, !pageMode);

  // 筛选项变化时回到第 1 页重新加载
  useEffect(() => {
    if (!pageMode) fetchList(1);
  }, [fetchList, pageMode]);

  // 角色选项来自后端 FrameworkRole 表，供 Add/Edit 弹窗多选使用
  useEffect(() => {
    listRoles()
      .then((res) => setRoleOptions(res.data ?? []))
      .catch(() => {
        /* 角色加载失败时弹窗显示空列表，不阻塞页面 */
      });
  }, []);

  // 分类目录：列表展示与弹窗选项共用
  useEffect(() => {
    if (pageMode) return;

    docCategoryApi
      .list()
      .then((categories) => {
        setCategoryNameById(Object.fromEntries(categories.map((c) => [c.id, c.name])));
        setVisibleToAllCategoryIds(
          categories.filter((c) => c.is_visible_to_all_users).map((c) => c.id),
        );
        setCategoryOptions(categories.map(({ id, name }) => ({ id, name })));
      })
      .catch(() => {
        /* 分类加载失败时权限列显示为空，不阻塞页面 */
      });
  }, [pageMode]);

  // React Router 会在用户列表与编辑路由之间复用同一个组件实例，
  // 因此每次路由目标变化都必须按当前 userId 重置编辑状态，不能沿用上一次的 useState 初始值。
  useEffect(() => {
    if (pageMode !== "edit") {
      setEditingUser(null);
      setEditLoading(false);
      setEditLoadUserId(null);
      setEditLoadError(null);
      setEditModalError(null);
      return;
    }

    setEditModalError(null);

    if (!userId) {
      setEditingUser(null);
      setEditLoading(false);
      setEditLoadUserId(null);
      setEditLoadError("User not found");
      return;
    }

    // 从列表进入编辑页时优先使用当前点击行；其 ID 必须与 URL 一致。
    if (routeEditingUser) {
      setEditingUser(routeEditingUser);
      setEditLoading(false);
      setEditLoadUserId(userId);
      setEditLoadError(null);
      return;
    }

    let cancelled = false;
    setEditingUser(null);
    setEditLoading(true);
    setEditLoadUserId(userId);
    setEditLoadError(null);

    findUserById(userId)
      .then((user) => {
        if (cancelled) return;
        if (user) setEditingUser(user);
        else setEditLoadError("User not found");
      })
      .catch((err: any) => {
        if (!cancelled) {
          setEditLoadError(err?.response?.data?.message || "Failed to load user");
        }
      })
      .finally(() => {
        if (!cancelled) setEditLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pageMode, routeEditingUser, userId]);

  useEffect(() => {
    if (pageMode || !routeState.toast) return;

    setToast(routeState.toast);
    navigate("/admin/users", { replace: true, state: null });
  }, [navigate, pageMode, routeState.toast]);

  const handleAddUser = async (newUser: User) => {
    const body: UserCreateRequest = {
      Name: newUser.name || undefined,
      Email: newUser.email,
      Roles: newUser.roles,
      Status: newUser.status,
      DocCategoryIds: newUser.docCategoryIds,
      Region: newUser.region,
    };
    setSubmitting(true);
    setAddModalError(null);
    try {
      await createUser(body);
      navigate("/admin/users", {
        state: {
          toast: {
            msg: `${t("users.toast.added") || "User added successfully"}`,
            kind: "success",
          },
        } satisfies UsersRouteState,
      });
    } catch (err: any) {
      setAddModalError(err?.response?.data?.message || "Failed to add user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async (id: string) => {
    if (statusActionUserId || deleteActionUserId) return;
    const target = users.find((u) => u.id === id);
    if (!target) return;
    setStatusActionUserId(id);
    try {
      const res = await toggleUserStatus(id);
      const nextStatus = res.data.Status;
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, status: nextStatus } : u)));
      showToast(
        nextStatus === "Active" ? `${t("users.toast.reEnabled")}` : `${t("users.toast.disabled")}`,
      );
    } catch (err: any) {
      showToast(err?.response?.data?.message || "Failed to disable user", "error");
    } finally {
      setStatusActionUserId(null);
    }
  };

  const handleSaveUser = async (updated: User) => {
    // DG 同步用户与 SSO 用户一样，姓名/邮箱由同步来源管理，不在编辑时提交
    const isSso = updated.source === "sso" || (updated.sources?.includes("dg") ?? false);
    const body: UserEditRequest = {
      Id: updated.id,
      Roles: updated.roles,
      Status: updated.status,
      // 权限与区域与用户来源无关，SSO / Manual 均可调整
      DocCategoryIds: updated.docCategoryIds,
      Region: updated.region,
    };
    if (!isSso) {
      body.Name = updated.name || "";
      body.Email = updated.email;
    }
    setSubmitting(true);
    setEditModalError(null);
    try {
      await updateUser(body);
      if (updated.id === currentUser?.id) {
        try {
          await refreshUser({ forceTokenRefresh: true });
        } catch {
          // Role 已保存且后端权限已实时生效；保留成功流程，由聚焦/轮询继续刷新身份。
        }
      }
      navigate("/admin/users", {
        state: {
          toast: {
            msg: `${t("users.toast.saved") || "User updated successfully"}`,
            kind: "success",
          },
        } satisfies UsersRouteState,
      });
    } catch (err: any) {
      setEditModalError(err?.response?.data?.message || "Failed to update user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (statusActionUserId || deleteActionUserId) return;
    const target = users.find((u) => u.id === id);
    if (!target) return;
    setDeleteActionUserId(id);
    try {
      await deleteUsers([id]);
      showToast(`${t("users.toast.deleted") || "User deleted successfully"}`);
      // 删除成功后只更新当前页面状态，避免重新请求导致整张表刷新。
      setUsers((prev) => prev.filter((user) => user.id !== id));
      setTotal((prev) => Math.max(0, prev - 1));
    } catch (err: any) {
      showToast(err?.response?.data?.message || "Failed to delete user", "error");
    } finally {
      setDeleteActionUserId(null);
    }
  };

  if (pageMode === "add") {
    return (
      <AdminLayout>
        <AddUserModal
          onClose={() => navigate("/admin/users")}
          onAdd={handleAddUser}
          roleOptions={roleOptions}
          submitting={submitting}
          serverError={addModalError}
        />
      </AdminLayout>
    );
  }

  if (pageMode === "edit") {
    const currentEditingUser =
      routeEditingUser ?? (editingUser?.id === userId ? editingUser : null);
    const currentEditLoadError =
      routeEditingUser || editLoadUserId !== userId ? null : editLoadError;
    const currentEditLoading = !currentEditingUser && (editLoadUserId !== userId || editLoading);

    if (currentEditLoading) {
      return (
        <AdminLayout>
          <div className="flex min-h-[calc(100vh-7rem)] items-center justify-center text-sm text-gray-400">
            <i className="ri-loader-4-line mr-2 animate-spin text-lg" />
            Loading user...
          </div>
        </AdminLayout>
      );
    }

    if (!currentEditingUser || currentEditLoadError) {
      return (
        <AdminLayout>
          <button
            type="button"
            onClick={() => navigate("/admin/users")}
            className="mb-3 inline-flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-gray-800"
          >
            <i className="ri-arrow-left-line text-base" />
            {t("common.back")}
          </button>
          <div className="rounded-xl border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-600">
            {currentEditLoadError || "User not found"}
          </div>
        </AdminLayout>
      );
    }

    return (
      <AdminLayout>
        <EditUserModal
          key={currentEditingUser.id}
          user={currentEditingUser}
          onClose={() => navigate("/admin/users")}
          onSave={handleSaveUser}
          roleOptions={roleOptions}
          submitting={submitting}
          serverError={editModalError}
        />
      </AdminLayout>
    );
  }

  const hasActiveFilters =
    search !== "" ||
    roleFilter !== "All" ||
    statusFilter !== "All" ||
    regionFilter !== "All" ||
    sourceFilter !== "All" ||
    generalSourceTypeFilter !== "All";

  const handleSearch = () => {
    const nextSearch = searchInput.trim();
    setSearch(nextSearch);
    fetchList(1, nextSearch);
  };

  const handleReset = () => {
    setSearchInput("");
    setSearch("");
    setRoleFilter("All");
    setStatusFilter("All");
    setRegionFilter("All");
    setSourceFilter("All");
    setGeneralSourceTypeFilter("All");
  };
  const normalizeDateTime = (str: string | null): string | null => {
    if (!str) return null;
    // Already in yyyy-mm-dd format
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str;
    // Handle slash format: 2026/8/20 14:54:10 → 2026-08-20 14:54:10
    const slashMatch = str.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})(.*)/);
    if (slashMatch) {
      const y = slashMatch[1];
      const m = slashMatch[2].padStart(2, "0");
      const d = slashMatch[3].padStart(2, "0");
      return `${y}-${m}-${d}${slashMatch[4] || ""}`;
    }
    return str;
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <AdminPageTitle title={t("users.title")} tooltip={t("users.subtitle")} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(isSyncing || lastSyncTime) && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mr-2">
              {isSyncing ? (
                <span
                  role="status"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap"
                  style={{ backgroundColor: "rgba(131,0,81,0.06)", color: "#830051" }}
                >
                  <i
                    className="ri-loader-4-line animate-spin text-xs"
                    style={{ color: "#830051" }}
                    aria-hidden="true"
                  ></i>
                  <span className="truncate max-w-[260px]">{t("syncConfig.syncing")}</span>
                </span>
              ) : (
                <>
                  <span>Last synced:</span>
                  <span className="font-medium">{lastSyncTime}</span>
                </>
              )}
            </div>
          )}
          <button
            onClick={() => navigate("/admin/users/dg-sync-settings")}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border transition-colors cursor-pointer whitespace-nowrap"
            style={{ color: "#830051", borderColor: "rgba(131,0,81,0.3)", backgroundColor: "#fff" }}
          >
            <i className="ri-settings-line text-sm"></i>
            DG Sync Settings
          </button>
          <button
            onClick={() => navigate("/admin/users/add")}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-lg transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: "#830051" }}
          >
            <i className="ri-user-add-line text-sm"></i>
            Add User
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Region */}
          <div className="relative">
            <select
              aria-label="Region"
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value as RegionFilter)}
              className="appearance-none text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-300 bg-white cursor-pointer"
            >
              <option value="All">All Regions</option>
              <option value="Mainland">Mainland</option>
              <option value="HK">HK</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
          </div>

          {/* 用户来源与 General Source Type 是不同维度，使用独立的 Source 请求参数。 */}
          <div className="relative">
            <select
              aria-label={t("users.source")}
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
              className="appearance-none text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-300 bg-white cursor-pointer"
            >
              <option value="All">{t("users.source.all")}</option>
              <option value="sso">SSO</option>
              <option value="dg">Distribution Group</option>
              <option value="manual">Manual</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
          </div>

          {/* Role */}
          <div className="relative">
            <select
              aria-label={t("users.role")}
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
              className="appearance-none text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-300 bg-white cursor-pointer"
            >
              <option value="All">{t("users.role.all")}</option>
              {roleOptions.map((role) => (
                <option key={role.Code} value={role.Code}>
                  {role.Name}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
          </div>

          {/* General Source Type */}
          <div className="relative">
            <select
              aria-label="General Source Type"
              value={generalSourceTypeFilter}
              onChange={(e) => setGeneralSourceTypeFilter(e.target.value)}
              className="appearance-none max-w-44 text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-300 bg-white cursor-pointer"
            >
              <option value="All">All Source Types</option>
              {categoryOptions.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
          </div>

          {/* Status */}
          <div className="relative">
            <select
              aria-label={t("users.status")}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="appearance-none text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-300 bg-white cursor-pointer"
            >
              <option value="All">{t("users.status.all")}</option>
              <option value="Active">{t("users.status.active")}</option>
              <option value="Disabled">{t("users.status.disabled")}</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
          </div>

          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
          >
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="search"
                placeholder={t("users.searchPlaceholder")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-40 lg:w-52 xl:w-72 pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-gray-300"
              />
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleReset}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line text-sm"></i>
                {t("common.reset")}
              </button>
            )}
            <button
              type="submit"
              className="px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: "#830051" }}
            >
              {t("common.search")}
            </button>
          </form>
        </div>

        <div className="flex-1" />
        <span className="text-xs text-gray-400 whitespace-nowrap">
          {loading ? "Loading…" : `${total} ${total !== 1 ? t("common.users") : t("common.user")}`}
        </span>
      </div>

      {/* Table */}
      <UsersTable
        users={users}
        categoryNameById={categoryNameById}
        visibleToAllCategoryIds={visibleToAllCategoryIds}
        loading={loading}
        onDisable={handleDisable}
        onEditUser={(user) =>
          navigate(`/admin/users/${user.id}/edit`, {
            state: { user } satisfies UsersRouteState,
          })
        }
        onDeleteUser={handleDeleteUser}
        statusActionUserId={statusActionUserId}
        deleteActionUserId={deleteActionUserId}
      />

      <AdminPagination
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        loading={loading}
        onPageChange={(nextPage) => void fetchList(nextPage)}
      />

      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl text-white text-sm font-medium"
          style={{ backgroundColor: toast.kind === "error" ? "#dc2626" : "#1f2937" }}
        >
          <i
            className={
              toast.kind === "error"
                ? "ri-error-warning-line"
                : "ri-checkbox-circle-line text-green-400"
            }
          ></i>
          {toast.msg}
        </div>
      )}
    </AdminLayout>
  );
}
